import { Controller } from "stimulus"
import Sortable from "sortablejs"
import { showToast } from "../utils/toast"

interface DraftStage {
  id?: string
  name: string
  color: string
  kind: string
  counts_as_interview: boolean
  count: number
}

interface DraftReason {
  id?: string
  label: string
}

const ICONS = {
  up: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 15l6-6 6 6"/></svg>',
  down: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 9l6 6 6-6"/></svg>',
  lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>',
  grip: '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>',
  trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>',
}

function escapeHtml(text: string): string {
  const div = document.createElement("div")
  div.textContent = text
  return div.innerHTML
}

export default class extends Controller {
  static targets = ["list", "reasons", "error", "saveDefault", "saveButton"]
  static values = { url: String, stages: Array, reasons: Array, palette: Array }

  declare readonly listTarget: HTMLElement
  declare readonly reasonsTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly saveDefaultTarget: HTMLInputElement
  declare readonly saveButtonTarget: HTMLButtonElement
  declare readonly urlValue: string
  declare readonly stagesValue: DraftStage[]
  declare readonly reasonsValue: DraftReason[]
  declare readonly paletteValue: string[]

  private stages: DraftStage[] = []
  private reasons: DraftReason[] = []
  private previousFocus: Element | null = null
  private sortable: Sortable | null = null

  connect(): void {
    this.stages = this.stagesValue.map((stage) => ({ ...stage }))
    this.reasons = this.reasonsValue.map((reason) => ({ ...reason }))
    this.previousFocus = document.activeElement
    this.render()
    this.renderReasons()
    this.sortable = Sortable.create(this.listTarget, {
      draggable: ".pl-st:not(.locked)",
      handle: ".grip",
      animation: 120,
      ghostClass: "is-ghost",
      onMove: (evt) => {
        const related = evt.related as HTMLElement
        if (related.dataset.kind === "entry" && !evt.willInsertAfter) return false
        if (related.dataset.kind === "final" && evt.willInsertAfter) return false
        return true
      },
      onEnd: (evt) => {
        if (evt.oldIndex === undefined || evt.newIndex === undefined || evt.oldIndex === evt.newIndex) return
        const [stage] = this.stages.splice(evt.oldIndex, 1)
        this.stages.splice(evt.newIndex, 0, stage)
        this.render()
      },
    })
    this.listTarget.querySelector<HTMLInputElement>("input[type=text]")?.focus()
  }

  disconnect(): void {
    this.sortable?.destroy()
    this.sortable = null
  }

  keydown(event: KeyboardEvent): void {
    if (event.key === "Escape") this.cancel()
  }

  cancel(): void {
    this.element.remove()
    if (this.previousFocus instanceof HTMLElement) this.previousFocus.focus()
  }

  up(event: Event): void {
    this.swap(this.indexOf(event), -1)
  }

  down(event: Event): void {
    this.swap(this.indexOf(event), 1)
  }

  recolor(event: Event): void {
    const input = event.currentTarget as HTMLInputElement
    if (!/^#[0-9a-f]{6}$/i.test(input.value)) return
    this.stages[this.indexOf(event)].color = input.value.toLowerCase()
    const swatch = input.closest<HTMLElement>(".sw")
    if (swatch) swatch.style.background = input.value
  }

  rename(event: Event): void {
    this.stages[this.indexOf(event)].name = (event.target as HTMLInputElement).value
  }

  interview(event: Event): void {
    const index = this.indexOf(event)
    this.stages.forEach((stage, i) => (stage.counts_as_interview = i === index))
  }

  remove(event: Event): void {
    const index = this.indexOf(event)
    const stage = this.stages[index]
    if (stage.kind !== "normal" || stage.count > 0) return
    this.stages.splice(index, 1)
    this.render()
  }

  add(): void {
    const palette = this.paletteValue
    const used = this.stages.length % palette.length
    const finalIndex = this.stages.findIndex((stage) => stage.kind === "final")
    const at = finalIndex < 0 ? this.stages.length : finalIndex
    this.stages.splice(at, 0, { name: "New stage", color: palette[used], kind: "normal", counts_as_interview: false, count: 0 })
    this.render()
    const input = this.listTarget.querySelectorAll<HTMLInputElement>("input[type=text]")[at]
    input?.focus()
    input?.select()
  }

  removeReason(event: Event): void {
    this.reasons.splice(this.indexOf(event), 1)
    this.renderReasons()
  }

  reasonKey(event: KeyboardEvent): void {
    if (event.key !== "Enter") return
    event.preventDefault()
    const input = event.target as HTMLInputElement
    const label = input.value.trim().replace(/\s+/g, " ")
    if (label && !this.reasons.some((reason) => reason.label.toLowerCase() === label.toLowerCase())) {
      this.reasons.push({ label })
    }
    this.renderReasons()
    this.reasonsTarget.querySelector<HTMLInputElement>("input")?.focus()
  }

  async save(): Promise<void> {
    this.saveButtonTarget.disabled = true
    this.errorTarget.hidden = true
    try {
      const response = await fetch(this.urlValue, {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          stages: this.stages.map(({ id, name, color, counts_as_interview }) => ({ id, name, color, counts_as_interview })),
          reasons: this.reasons,
          save_as_default: this.saveDefaultTarget.checked,
        }),
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        this.showError((json.errors as string[] | undefined)?.join(" ") || "Couldn't save the stages. Reload and try again.")
        return
      }
      showToast("Stages saved")
      window.location.reload()
    } catch {
      this.showError("Couldn't reach the server. Try again.")
    } finally {
      this.saveButtonTarget.disabled = false
    }
  }

  private showError(message: string): void {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  private indexOf(event: Event): number {
    return Number((event.currentTarget as HTMLElement).dataset.index)
  }

  private movable(index: number, direction: number): boolean {
    const target = index + direction
    const stage = this.stages[index]
    const other = this.stages[target]
    return !!stage && !!other && stage.kind === "normal" && other.kind === "normal"
  }

  private swap(index: number, direction: number): void {
    if (!this.movable(index, direction)) return
    const [stage] = this.stages.splice(index, 1)
    this.stages.splice(index + direction, 0, stage)
    this.render()
  }

  private deleteTitle(stage: DraftStage): string {
    if (stage.kind === "entry") return "The entry stage can't be removed"
    if (stage.kind === "final") return "The final stage can't be removed"
    if (stage.count > 0) return `Move the ${stage.count} candidate${stage.count === 1 ? "" : "s"} out of this stage first`
    return "Remove stage"
  }

  private render(): void {
    this.listTarget.innerHTML = this.stages
      .map((stage, i) => {
        const locked = stage.kind !== "normal"
        const tag =
          stage.kind === "entry"
            ? `<span class="tag">${ICONS.lock}Entry · from outreach</span>`
            : stage.kind === "final"
              ? `<span class="tag">${ICONS.lock}Final</span>`
              : `<label class="pl-int" title="Counts as an interview for the Goal"><input type="radio" name="pl-interview" data-index="${i}" data-action="change->pipeline-stages-modal#interview"${stage.counts_as_interview ? " checked" : ""}> Interview</label>`
        const canDelete = stage.kind === "normal" && stage.count === 0
        return (
          `<div class="pl-st${locked ? " locked" : ""}" data-kind="${stage.kind}">` +
          (locked ? `<span class="grip is-locked" aria-hidden="true"></span>` : `<span class="grip" title="Drag to reorder" aria-hidden="true">${ICONS.grip}</span>`) +
          `<div class="mv"><button type="button" aria-label="Move up" data-index="${i}" data-action="pipeline-stages-modal#up"${this.movable(i, -1) ? "" : " disabled"}>${ICONS.up}</button>` +
          `<button type="button" aria-label="Move down" data-index="${i}" data-action="pipeline-stages-modal#down"${this.movable(i, 1) ? "" : " disabled"}>${ICONS.down}</button></div>` +
          `<label class="sw" style="background:${escapeHtml(stage.color)}" title="Change colour">` +
          `<input type="color" value="${escapeHtml(stage.color)}" aria-label="Colour of ${escapeHtml(stage.name)}" data-index="${i}" data-action="input->pipeline-stages-modal#recolor"></label>` +
          `<input type="text" maxlength="60" value="${escapeHtml(stage.name)}" aria-label="Stage name" data-index="${i}" data-action="input->pipeline-stages-modal#rename">` +
          tag +
          `<span class="n">${stage.count > 0 ? `${stage.count} here` : "empty"}</span>` +
          `<button type="button" class="del" title="${this.deleteTitle(stage)}" aria-label="${this.deleteTitle(stage)}" data-index="${i}" data-action="pipeline-stages-modal#remove"${canDelete ? "" : " disabled"}>${ICONS.trash}</button>` +
          `</div>`
        )
      })
      .join("")
  }

  private renderReasons(): void {
    this.reasonsTarget.innerHTML =
      this.reasons
        .map(
          (reason, i) =>
            `<span class="r">${escapeHtml(reason.label)}<button type="button" aria-label="Remove ${escapeHtml(reason.label)}" data-index="${i}" data-action="pipeline-stages-modal#removeReason">×</button></span>`,
        )
        .join("") +
      `<input type="text" maxlength="80" placeholder="+ Add reason, press Enter" aria-label="Add a rejection reason" data-action="keydown->pipeline-stages-modal#reasonKey">`
  }
}
